import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  Configuration as MarketMakingApiConfiguration,
  CrossObjectsControllerService,
  HedgeTradesControllerService,
  ManagingControllerService,
  MarketMakingPairSettingsControllerService,
  MarketMakingPairStatusControllerService,
  MarketMakingRoutesControllerService,
  MarketMakingSecondLegRoutesControllerService,
  MarketMakingSecondLegSettingsControllerService,
  MarketMakingSecondLegStatusesControllerService,
  MarketMakingTradesControllerService,
  PlatformBalancesControllerService,
  SystemSettingsControllerService,
} from '@api/market-making';
import { AuthService } from 'app/auth/services/auth.service';

// one shared generated client, so platforms differ only by base path and need their own instances
const MARKET_MAKING_SERVICE_CTORS = {
  crossObjects: CrossObjectsControllerService,
  managing: ManagingControllerService,
  pairSettings: MarketMakingPairSettingsControllerService,
  routes: MarketMakingRoutesControllerService,
  balances: PlatformBalancesControllerService,
  pairStatuses: MarketMakingPairStatusControllerService,
  secondLegSettings: MarketMakingSecondLegSettingsControllerService,
  secondLegRoutes: MarketMakingSecondLegRoutesControllerService,
  secondLegStatuses: MarketMakingSecondLegStatusesControllerService,
  systemSettings: SystemSettingsControllerService,
  trades: MarketMakingTradesControllerService,
  hedgeTrades: HedgeTradesControllerService,
} as const;

type MarketMakingServiceKey = keyof typeof MARKET_MAKING_SERVICE_CTORS;

export type MarketMakingServices = {
  [K in MarketMakingServiceKey]: InstanceType<(typeof MARKET_MAKING_SERVICE_CTORS)[K]>;
};

const serviceKeys = Object.keys(MARKET_MAKING_SERVICE_CTORS) as MarketMakingServiceKey[];

@Injectable({ providedIn: 'root' })
export class MarketMakingServicesFactory {
  private readonly _httpClient = inject(HttpClient);
  private readonly _authService = inject(AuthService);

  private readonly _byBasePath = new Map<string, MarketMakingServices>();

  public forBasePath(apiBasePath: string): MarketMakingServices {
    const cached = this._byBasePath.get(apiBasePath);
    if (cached) {
      return cached;
    }

    const configuration = new MarketMakingApiConfiguration({
      basePath: apiBasePath,
      accessToken: () => this._authService.getToken(),
    });

    const services = {} as MarketMakingServices;
    for (const key of serviceKeys) {
      const ServiceCtor = MARKET_MAKING_SERVICE_CTORS[key];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (services as any)[key] = new ServiceCtor(this._httpClient, apiBasePath, configuration);
    }

    this._byBasePath.set(apiBasePath, services);
    return services;
  }
}
